import * as React from 'react';
import { Link } from '@tanstack/react-router';

import {
  useAuth,
  type AuthRole,
  type SupplierContactRole,
} from '@/auth/auth-context';

// =============================================================================
// sidebar.tsx — Navigation principale role-gated (D3a, S20-REMEDIATION).
// =============================================================================
// Structure dérivée du DBU (« barre de navigation principale », NAV_GROUPS §2.x/§3.x)
// + inventaire §7. ROLE-GATED : menu ADMIN (3 groupes) vs FOURNISSEUR selon le rôle de
// session. PROVISOIRE : le rôle vient de demoAuthState (defaut 'admin') ; il sera renseigné
// par la session OIDC/magic-link réelle (DV-OIDC-HYDRA-TEST) — le composant est déjà prêt.
// SEC-NAV-01 : ce composant n'est JAMAIS rendu hors session — il vit uniquement sous la
// layout route « authenticated » (router.tsx), jamais sur les écrans pré-auth. Garanti par
// le routage, pas par une garde interne ici.
//
// On ne lie QUE des routes EXISTANTES (router.tsx). Les écrans fournisseur non encore
// implémentés (Tableau de bord, Mes campagnes, Mon entreprise, Mon équipe — D3b « vrais
// trous ») ne sont PAS liés ici pour éviter des 404 ; ils s'ajouteront à leur livraison.
//
// WCAG 2.1 AA : <nav aria-label>, groupes titrés, aria-current='page' sur l'actif (via
// activeProps TanStack Router), focus visible (tokens shadcn/Tailwind amber-minimal).

/**
 * Un lien de navigation. `search` (optionnel) porte un pré-filtre d'URL (ex. `{statut:'archive'}`) ;
 * `activeOptions` (optionnel) affine la mise en évidence de l'actif — utile pour DISTINGUER deux liens
 * de MÊME path différenciés par le search (ex. « En cours » vs « Archivés » sur `/echanges`).
 */
interface NavLink {
  readonly label: string;
  readonly to: string;
  readonly search?: Record<string, unknown>;
  readonly activeOptions?: {
    readonly exact?: boolean;
    readonly includeSearch?: boolean;
    readonly explicitUndefined?: boolean;
  };
}
/**
 * Un SOUS-GROUPE (label + enfants) — rendu en sous-titre + liste indentée de liens, NON repliable
 * (toujours visible). A11y : `<ul>` imbriqué étiqueté par le sous-titre (aria-labelledby), aucune
 * information portée par la seule couleur (le libellé texte + aria-current suffisent).
 */
interface NavSubGroup {
  readonly subGroupLabel: string;
  readonly children: readonly NavLink[];
}
type NavEntry = NavLink | NavSubGroup;
/** Un sous-groupe se distingue d'un lien par la présence de `children` (garde de type). */
function isSubGroup(entry: NavEntry): entry is NavSubGroup {
  return 'children' in entry;
}
interface NavGroup {
  readonly title: string;
  readonly items: readonly NavEntry[];
}

/** Menu ADMIN (DBU §2.x « Menu Administration / Campagnes / … »). Routes existantes seules. */
const ADMIN_NAV: readonly NavGroup[] = [
  {
    title: 'Pilotage',
    items: [
      { label: 'Tableau de bord', to: '/admin' },
      { label: 'Campagnes', to: '/admin/campaigns' },
      { label: 'Évolution par client', to: '/admin/campaigns/rank-client-evolution' },
    ],
  },
  {
    title: 'Référentiels',
    items: [
      { label: 'Fournisseurs', to: '/admin/suppliers' },
      { label: 'Clients', to: '/admin/clients' },
      { label: 'Catalogue', to: '/admin/catalog/versions' },
      { label: 'Compétences', to: '/admin/catalog/skills' },
    ],
  },
  {
    title: 'Communication & cadre',
    items: [
      { label: 'Conversations', to: '/admin/conversations' },
      { label: 'Annonces', to: '/admin/announcements' },
      { label: 'Base de connaissance', to: '/admin/knowledge-base' },
      { label: 'CGU', to: '/admin/cgu' },
      { label: 'Exports référentiel', to: '/admin/exports' },
      { label: 'Export conformité', to: '/admin/compliance/audit' },
      { label: 'Paramètres', to: '/parameters' },
    ],
  },
];

/**
 * Menu FOURNISSEUR (structure GATE_HUMAN, redesign 2026-09-12). Groupes : « Mon Espace » (tableau de
 * bord, historique, « Mes échanges avec JeKo » → /echanges) et « Ma Documentation » (catalogue profil,
 * FAQ). Les échanges pointent la page unifiée `/echanges` en UNE SEULE entrée (« une seule voix ») :
 * l'ancien sous-groupe En cours / Archivés est retiré. Le param `statut=archive` de la route reste
 * SUPPORTÉ (pré-filtre du chip « Archivées ») mais n'est plus câblé au menu. La FAQ est une page
 * autonome `/faq` ; les CGU restent accessibles par le FOOTER.
 */
const SUPPLIER_NAV: readonly NavGroup[] = [
  {
    title: 'Mon Espace',
    items: [
      { label: 'Tableau de bord', to: '/dashboard' },
      { label: 'Historique', to: '/history' },
      { label: 'Mes échanges avec JeKo', to: '/echanges' },
    ],
  },
  {
    title: 'Ma Documentation',
    items: [
      { label: 'Catalogue profil', to: '/catalog' },
      { label: 'FAQ', to: '/faq' },
    ],
  },
];

/**
 * Groupe « Mon Entreprise » RÉSERVÉ AU MASTER (§3.7 : « Menu Mon entreprise > Mes collaborateurs »,
 * visible uniquement pour le contact MASTER). Filtre UI ; l'ACL réelle est côté API (§3.7 MASTER-only).
 */
const SUPPLIER_MASTER_NAV: NavGroup = {
  title: 'Mon Entreprise',
  // S37-ANNUAIRE-UX #18 — libellé aligné DBU l.3086 (« Mes collaborateurs », et non « Mon équipe »).
  items: [{ label: 'Mes collaborateurs', to: '/collaborateurs' }],
};

/**
 * Groupe « Mes Campagnes » (GATE_HUMAN 2026-09-12) : UNE SEULE voix « Mes campagnes » → /campagnes.
 * Le filtre En cours / Archivées est désormais porté DANS la page (contrôle « Statut »), plus par le menu.
 */
const SUPPLIER_CAMPAIGNS_NAV: NavGroup = {
  title: 'Mes Campagnes',
  items: [{ label: 'Mes campagnes', to: '/campagnes' }],
};

function navForRole(
  role: AuthRole,
  supplierRole: SupplierContactRole | undefined,
): readonly NavGroup[] {
  if (role !== 'supplier') {
    return ADMIN_NAV;
  }
  // Ordre GATE_HUMAN : Mon Espace, Ma Documentation, Mon Entreprise (MASTER only), Mes Campagnes.
  // La zone « Mon Entreprise » n'apparaît QUE pour le MASTER (un collaborateur ne la voit pas, §3.7).
  return supplierRole === 'MASTER'
    ? [...SUPPLIER_NAV, SUPPLIER_MASTER_NAV, SUPPLIER_CAMPAIGNS_NAV]
    : [...SUPPLIER_NAV, SUPPLIER_CAMPAIGNS_NAV];
}

const NAV_LINK_BASE =
  'block rounded-md px-2 py-1.5 text-sm text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring';
const NAV_LINK_ACTIVE =
  'block rounded-md px-2 py-1.5 text-sm font-medium bg-sidebar-accent text-sidebar-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring';

/**
 * Un lien de navigation (TanStack `<Link>`). L'état actif est porté par `activeProps` (fond accentué +
 * `aria-current='page'`) — jamais par la seule couleur (WCAG 1.4.1). `search`/`activeOptions` sont propagés
 * quand présents (pré-filtre d'URL + distinction fine de l'actif pour les liens de même path).
 */
function NavLinkAnchor({ link }: { readonly link: NavLink }): React.JSX.Element {
  // `exact` par défaut pour /admin (racine admin — sinon actif sur tous ses sous-chemins).
  const activeOptions = link.activeOptions ?? { exact: link.to === '/admin' };
  return (
    <Link
      to={link.to}
      {...(link.search !== undefined ? { search: link.search } : {})}
      className={NAV_LINK_BASE}
      activeProps={{ className: NAV_LINK_ACTIVE, 'aria-current': 'page' }}
      activeOptions={activeOptions}
    >
      {link.label}
    </Link>
  );
}

/**
 * Un sous-groupe NON repliable : sous-titre + `<ul>` imbriqué et indenté de liens (toujours visible).
 * A11y : le `<ul>` imbriqué est étiqueté par le sous-titre (`aria-labelledby`) ; l'actif reste porté par
 * `aria-current='page'` (le libellé texte + le fond accentué doublent l'information, jamais la couleur seule).
 */
function NavSubGroupItem({
  subGroup,
}: {
  readonly subGroup: NavSubGroup;
}): React.JSX.Element {
  const labelId = `nav-subgroup-${subGroup.subGroupLabel.replace(/\s+/g, '-').toLowerCase()}`;
  return (
    <li>
      <span
        id={labelId}
        className="block px-2 pb-0.5 pt-1.5 text-xs font-medium text-muted-foreground"
      >
        {subGroup.subGroupLabel}
      </span>
      <ul aria-labelledby={labelId} className="flex flex-col gap-0.5 pl-3">
        {subGroup.children.map((child) => (
          <li key={child.to + child.label}>
            <NavLinkAnchor link={child} />
          </li>
        ))}
      </ul>
    </li>
  );
}

/** Barre de navigation principale, role-gated. Rendue dans l'AppShell (colonne gauche 240px,
 *  sticky pleine hauteur). Marque en haut, groupes de navigation, et — côté FOURNISSEUR — la
 *  carte « Mon compte » (.sb-org du DS) rappelant QUI est connecté (P-6, 2026-09-23 : elle
 *  rappelait le SIRET, que l'utilisateur ne se reconnaît pas ; la raison sociale de l'entreprise
 *  a sa place en haut à droite, pas ici). */
export function Sidebar(): React.JSX.Element {
  const auth = useAuth();
  const role: AuthRole = auth.role ?? 'admin';
  const isSupplier = role === 'supplier';
  const groups = navForRole(role, auth.supplierRole);

  return (
    <nav
      aria-label="Navigation principale"
      className="sticky top-0 flex h-screen flex-col border-r border-sidebar-border bg-sidebar text-sidebar-foreground"
    >
      {/* Marque — alignée sur la hauteur de la topbar (56px). */}
      <div className="flex h-14 shrink-0 items-center border-b border-sidebar-border px-4">
        <span className="font-mono text-sm font-semibold tracking-tight">JEKO Référencement</span>
      </div>

      <div className="flex flex-1 flex-col gap-6 overflow-y-auto px-4 py-6">
        {groups.map((group) => (
          <div key={group.title} className="flex flex-col gap-1">
            <h2 className="px-2 font-mono text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
              {group.title}
            </h2>
            <ul className="flex flex-col gap-0.5">
              {group.items.map((entry) =>
                isSubGroup(entry) ? (
                  <NavSubGroupItem key={entry.subGroupLabel} subGroup={entry} />
                ) : (
                  <li key={entry.to + entry.label}>
                    <NavLinkAnchor link={entry} />
                  </li>
                ),
              )}
            </ul>
          </div>
        ))}
        {/* GATE_HUMAN 2026-09-12 : la liste des campagnes en cours dans la sidebar (ancien
            SupplierCampaignSwitcher, R15-UX-01) est RETIRÉE — la sélection se fait dans la page /campagnes. */}
      </div>

      {/* Carte « Mon compte » (.sb-org DS) — FOURNISSEUR uniquement : QUI est connecté.
          P-6 : le nom vient de GET /supplier/contacts/me, que le bootstrap appelait déjà. Tant qu'il
          n'est pas résolu (contact sans nom ni prénom), on retombe sur le SIRET en chasse fixe plutôt
          que de laisser un cadre vide : mieux vaut un identifiant qu'un blanc inexplicable. Le SIRET
          reste de toute façon en infobulle, c'est lui qu'on donne au support. */}
      {isSupplier && auth.tenantId ? (
        <div className="shrink-0 border-t border-sidebar-border p-4">
          <div className="rounded-lg border border-sidebar-border bg-card p-3">
            <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
              Mon compte
            </p>
            <p
              className={
                auth.name !== undefined
                  ? 'mt-1 text-xs font-medium text-sidebar-foreground'
                  : 'mt-1 font-mono text-xs text-sidebar-foreground'
              }
              title={auth.tenantId}
            >
              {auth.name ?? auth.tenantId}
            </p>
            {auth.supplierRole !== undefined ? (
              <p className="mt-0.5 text-[10px] text-muted-foreground">
                {auth.supplierRole === 'MASTER' ? 'Référent' : 'Collaborateur'}
              </p>
            ) : null}
          </div>
        </div>
      ) : null}
    </nav>
  );
}
